import { useState, useCallback, useRef, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Alert,
  Divider,
  Typography,
  CircularProgress,
  Tooltip,
  Button,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import { useLanguage } from '../../../context/LanguageContext';
import { useLayoutPreferences } from '../../../context/LayoutPreferencesContext';
import { PageContainer, PageTitle } from '../../../components/Common';
import CommunicationDialog from './communication/CommunicationDialog';
import CommunicationResumeCard, { CommunicationResumeData } from './resume/CommunicationResumeCard';
import AnimationForm from './AnimationForm';
import {
  useCreateAnimation,
  useGetAnimationById,
  useDeleteCommunicationInput,
  AnimationInputData,
  CommunicationInputData,
} from '../../../hooks/Marketing';

const CHANNEL_LABELS: Record<number, string> = { 1: 'courrier', 2: 'email', 3: 'sms', 4: 'tikeo', 5: 'cashdesk' };

const STATUS_LABELS: Record<number, 'Brouillon' | 'Planifiée' | 'Envoyée'> = {
  1: 'Brouillon', 2: 'Brouillon', 3: 'Planifiée', 4: 'Planifiée', 5: 'Envoyée',
};

const formatDate = (iso: string | null | undefined): string => {
  if (!iso) return 'Envoi non programmé';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return 'Envoi non programmé';
  return (
    String(d.getDate()).padStart(2, '0') + '/' +
    String(d.getMonth() + 1).padStart(2, '0') + '/' +
    d.getFullYear() + ' ' +
    String(d.getHours()).padStart(2, '0') + 'h' +
    String(d.getMinutes()).padStart(2, '0')
  );
};

const formatDateRange = (start: string | null, end: string | null): string => {
  const fmt = (iso: string | null) => {
    if (!iso) return '?';
    const d = new Date(iso);
    if (isNaN(d.getTime())) return '?';
    return String(d.getDate()).padStart(2, '0') + '/' + String(d.getMonth() + 1).padStart(2, '0') + '/' + d.getFullYear();
  };
  return fmt(start) + ' - ' + fmt(end);
};

const mapCommToResume = (comm: CommunicationInputData): CommunicationResumeData => {
  const actionStatusId = comm.action?.actionStatusId ?? (comm.isValidated ? 3 : 1);
  return {
    id: String(comm.id),
    title: comm.defTitle,
    scheduledDate: formatDate(comm.defSendStart),
    status: STATUS_LABELS[actionStatusId] ?? 'Brouillon',
    nbRecipients: comm.action?.nbRecipients ? comm.action.nbRecipients.toLocaleString('fr-FR') : '—',
    sendByCustomer: comm.defChannelId ? CHANNEL_LABELS[comm.defChannelId] === 'cashdesk' : false,
    templateAssociated: !!comm.creationConfigId,
    offers: comm.offerInputs.map((o) => ({
      name: o.title ?? ('Offre #' + (o.offerId ?? o.id)),
      scheduledDate: formatDateRange(o.startValidityDate, o.endValidityDate),
    })),
  };
};

function NewAnimation() {
  const { t } = useLanguage();
  const { sidebarColor } = useLayoutPreferences();

  const { createAnimation, isLoading: saving, error: saveError } = useCreateAnimation();
  const { animation: currentAnimation, setAnimation: setCurrentAnimation, isLoading: loadingAnim, fetchAnimation } = useGetAnimationById();
  const { deleteCommunicationInput } = useDeleteCommunicationInput();

  const formValuesRef = useRef({ titre: '', description: '' });
  const currentAnimationRef = useRef<AnimationInputData | null>(null);
  useEffect(() => { currentAnimationRef.current = currentAnimation; }, [currentAnimation]);
  const [hasTitre, setHasTitre] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogKey, setDialogKey] = useState(0);
  const [editingComm, setEditingComm] = useState<CommunicationInputData | null>(null);
  const [alertDismissed, setAlertDismissed] = useState(false);

  const handleFormValuesChange = useCallback((titre: string, desc: string) => {
    formValuesRef.current = { titre, description: desc };
  }, []);

  const refreshComms = useCallback(() => {
    const id = currentAnimationRef.current?.id;
    if (id) fetchAnimation(id);
  }, [fetchAnimation]);

  const handleEnsureAnimationId = useCallback(async (): Promise<number | null> => {
    if (currentAnimationRef.current !== null) return currentAnimationRef.current.id;
    const { titre, description } = formValuesRef.current;
    if (titre.trim() === '') return null;
    const result = await createAnimation({ title: titre.trim(), description: description.trim() || undefined });
    if (result) {
      const anim = result as AnimationInputData;
      currentAnimationRef.current = anim;
      setCurrentAnimation(anim);
      return anim.id;
    }
    return null;
  }, [createAnimation, setCurrentAnimation]);

  const handleSave = useCallback(async () => {
    const { titre, description } = formValuesRef.current;
    if (titre.trim() === '') return;
    const result = await createAnimation({ title: titre.trim(), description: description.trim() || undefined });
    if (result) {
      const anim = result as AnimationInputData;
      currentAnimationRef.current = anim;
      setCurrentAnimation(anim);
    }
  }, [createAnimation, setCurrentAnimation]);

  const handleReset = useCallback(() => {
    currentAnimationRef.current = null;
    setCurrentAnimation(null);
    formValuesRef.current = { titre: '', description: '' };
    setHasTitre(false);
  }, [setCurrentAnimation]);

  const handleOpenNew = () => {
    setEditingComm(null);
    setDialogKey(k => k + 1);
    setDialogOpen(true);
  };

  const handleOpenEdit = (id: string) => {
    const comm = currentAnimation?.communicationInputs.find(c => String(c.id) === id) ?? null;
    setEditingComm(comm);
    setDialogKey(k => k + 1);
    setDialogOpen(true);
  };

  const handleDelete = useCallback(async (id: string) => {
    const ok = await deleteCommunicationInput(Number(id));
    if (ok) {
      refreshComms();
    } else {
      console.error('[handleDelete] La suppression de la communication a échoué');
    }
  }, [deleteCommunicationInput, refreshComms]);

  const handleDialogClose = useCallback(() => {
    setDialogOpen(false);
    setEditingComm(null);
  }, []);

  const handleDialogSaved = useCallback(() => {
    setDialogOpen(false);
    setEditingComm(null);
    refreshComms();
  }, [refreshComms]);

  const communications = currentAnimation?.communicationInputs ?? [];

  return (
    <PageContainer>
      <PageTitle>{t('pages.marketing.newAnimation')}</PageTitle>

      <Card variant="outlined" sx={{ borderRadius: 2 }}>
        <CardContent sx={{ p: 3 }}>

          {alertDismissed === false && (
            <Alert severity="info" onClose={() => setAlertDismissed(true)} sx={{ mb: 3 }}>
              Rappel : Associez plusieurs communications à votre animation marketing pour un impact maximal.
              Chaque communication peut cibler un segment différent de vos clients.
            </Alert>
          )}

          <AnimationForm
            currentAnimation={currentAnimation}
            saving={saving}
            saveError={saveError}
            onHasTitreChange={setHasTitre}
            onValuesChange={handleFormValuesChange}
            onSave={handleSave}
            onReset={handleReset}
          />

          <Divider sx={{ my: 3 }} />

          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
            <Typography variant="subtitle1" fontWeight={700} sx={{ letterSpacing: 0.5 }}>
              Communication{communications.length > 1 ? 's' : ''}
              {communications.length > 0 && (
                <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                  ({communications.length})
                </Typography>
              )}
            </Typography>
            <Tooltip title={!hasTitre ? 'Renseignez d\'abord un titre pour l\'animation' : ''}>
              <span>
                <Button
                  variant="outlined"
                  startIcon={<AddIcon />}
                  onClick={handleOpenNew}
                  disabled={!hasTitre}
                  sx={{
                    color: sidebarColor,
                    borderColor: sidebarColor,
                    textTransform: 'none',
                    fontWeight: 600,
                    '&:hover': { borderColor: sidebarColor, backgroundColor: sidebarColor + '12' },
                  }}
                >
                  Ajouter une communication
                </Button>
              </span>
            </Tooltip>
          </Box>

          {!hasTitre ? (
            <Typography variant="body2" color="text.secondary" sx={{ py: 2, textAlign: 'center' }}>
              Renseignez un titre pour commencer.
            </Typography>
          ) : loadingAnim ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
              <CircularProgress size={28} />
            </Box>
          ) : communications.length === 0 ? (
            <Typography variant="body2" color="text.secondary" sx={{ py: 2, textAlign: 'center' }}>
              Aucune communication pour cette animation. Cliquez sur « Ajouter une communication » pour commencer.
            </Typography>
          ) : (
            communications.map((comm) => (
              <CommunicationResumeCard
                key={comm.id}
                data={mapCommToResume(comm)}
                onEdit={handleOpenEdit}
                onDelete={handleDelete}
              />
            ))
          )}
        </CardContent>
      </Card>

      <CommunicationDialog
        key={dialogKey}
        open={dialogOpen}
        onClose={handleDialogClose}
        onSaved={handleDialogSaved}
        animationInputId={currentAnimation?.id ?? null}
        onEnsureAnimationId={handleEnsureAnimationId}
        editingComm={editingComm}
      />
    </PageContainer>
  );
}

export default NewAnimation;
